import type { ComponentProps, FC, PropsWithChildren } from 'react';

import { Box, Card, Image, PromotionalCard, Stack } from '@eneco/sparky';
import type { HeadingProps } from '@eneco/sparky/types';

import { headerClass, headerPromotionalCardClass } from './CampaignCard.css';

type PromotionalCardTitle = ComponentProps<typeof PromotionalCard>['title'];
export type CampaignCardBackground = ComponentProps<typeof PromotionalCard>['background'];
export type CampaignCardObjectFit = ComponentProps<typeof Image>['objectFit'];

interface CampaignCardImage {
  src?: string;
  alt?: string;
  srcSet?: string;
}

export interface CampaignCardProps {
  promotionalTitle?: PromotionalCardTitle;
  image?: CampaignCardImage;
  headingLevel: HeadingProps['as'];
  background: CampaignCardBackground;
  objectFit: CampaignCardObjectFit;
  isElevated: boolean;
}

const ElevatedWrapper: FC<PropsWithChildren<{ isBrandBackground: boolean }>> = ({ isBrandBackground, children }) => (
  <div data-testid="content-card-elevated">
    <Card overflow={isBrandBackground ? undefined : 'hidden'}>
      <Box backgroundColor="backgroundPrimary">{children}</Box>
    </Card>
  </div>
);

/**
 * Content card with a PromotionalCard header (text-driven sticker over a square image)
 * instead of a regular heading. Body copy and CTAs are passed as children.
 */
export const CampaignCard: FC<PropsWithChildren<CampaignCardProps>> = ({
  promotionalTitle,
  image,
  headingLevel,
  background,
  objectFit,
  isElevated,
  children,
}) => {
  // Brand sticker can overhang the square frame — don't clip it.
  const isBrandBackground = background === 'brand';

  const card = (
    // Sparky space 6 = 24px (4px grid).
    <Box padding="6">
      <Stack gap="6">
        {promotionalTitle || image?.src ? (
          <Box
            className={headerClass({ overflow: isBrandBackground ? 'visible' : 'hidden' })}
            backgroundColor="backgroundSecondary">
            <PromotionalCard
              backdropBorderRadius="s"
              className={headerPromotionalCardClass}
              title={promotionalTitle}
              headingAs={headingLevel}
              background={background}
              size="s">
              {image?.src ? (
                <Image
                  alt={image.alt ?? ''}
                  height="100%"
                  objectFit={objectFit}
                  sizes={{ initial: '100%' }}
                  src={image.src}
                  srcSet={image.srcSet ?? ''}
                  width="100%"
                />
              ) : null}
            </PromotionalCard>
          </Box>
        ) : null}

        {children}
      </Stack>
    </Box>
  );

  return isElevated ? <ElevatedWrapper isBrandBackground={isBrandBackground}>{card}</ElevatedWrapper> : card;
};

export default CampaignCard;
